import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Appointment } from '../../../landing/models';
import { LandingPageService } from '../../../services/landing-page.service';
import moment from 'moment';
import { Globals } from '../../../globals';
import { HomeAccordionService } from '../controls/home-accordion/home-accordion.service';
import { Subscription, interval } from 'rxjs';
import { MessagingService } from '../../../services/messaging.service';
import { CommMethodEnum } from '../../../models/enum/comm-method.enum';
import { MessagingConstants } from '../../../models/MessagingConstants';
import { PatientService } from '../../../modules/patient/patient.service';
import { PatientDataModel } from '../../../models/patient/patient-data.model';
import { LocalStorageService } from '../../../services/local-storage.service';
import { FileManagerService } from '../../../modules/file-manager/file-manager.service';
import { DocumentDataModel } from '../../../models/document/document-data.model';
import { SignalrService } from 'src/app/services/socket/signalr.service';
import {UiService} from '../../../services/ui.service';

@Component({
  standalone: false,
  selector: 'app-recent-patients',
  templateUrl: './recent-patients.component.html',
  styleUrls: ['./recent-patients.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecentPatientsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() recentPatients: PatientDataModel[] = [];
  @Input() isOpen: boolean = false;

  @Output() requestClose = new EventEmitter();
  // this is for the schedule section on the top left
  // it's suppose to default at today and can only go in the future
  currAndFutureAppointments: Appointment[] = [];
  // this is for the recent patients, it starts at yesterday only goes back in time
  pastAppointments: Appointment[] = [];
  selectedAppointmentId: string;

  recentPatientRange: string = '';

  apptPageSize: number = 10;
  apptPageIndex: number = 0;

  scheduleDate: moment.Moment;
  today: moment.Moment = moment();
  isLoading: boolean = true;
  isPrivateInfoVisible: boolean = false;

  private privacyMonitorSubscription: Subscription;
  private previousPrivacySetting: boolean;


  constructor(private landingPageService: LandingPageService,
    private fileService: FileManagerService,
    public patientService: PatientService,
    private globals: Globals,
    private homeAccordionService: HomeAccordionService,
    private localStorageService: LocalStorageService,
    private cd: ChangeDetectorRef,
    private messagingService: MessagingService,
    private uiService: UiService,
    private signalrService: SignalrService) { }

  ngOnInit(): void {
    // Initialize with current data if available
    this.recentPatients = [...this.patientService.recentPatients];
    this.isPrivateInfoVisible = !this.globals.PrivacyEnabled;
    this.previousPrivacySetting = this.globals.PrivacyEnabled;

    // Setup privacy monitoring
    this.setupPrivacyMonitoring();

    // Listen for patient deleted messages
    this.messagingService.componentMessageCall.subscribe(response => {
      if (response.event === 'PATIENT_DELETED') {
        // If we know which patient was deleted, we can manually filter it out for immediate UI update
        if (response.data && response.data.patientId) {
          this.recentPatients = this.recentPatients.filter(
            patient => patient.PatientId !== response.data.patientId
          );
          this.cd.markForCheck();
          this.cd.detectChanges();
        }
      }
    });

    this.signalrService.messageUser.subscribe((res) => {
      if (res.messageType === 'UPDATE_PATIENT_PICTURE') {
      if (res.message && res.message['patientId'] && res.message['patientId'] === this.globals.currentPatientId) {
        this.fileService.getPatientPictureClearCache(this.globals.currentPatientId).subscribe((res2) => {
        this.fileService.getPatientPicture(this.globals.currentPatientId + '?' + new Date().toString()).subscribe((res) => {
          if (res) {
          const patient = this.recentPatients.find(x => x.PatientId === this.globals.currentPatientId);
          if (patient) {
            patient.patientImageUrl = this.fileService.getDocumentUrlFromDocument(res);
            this.cd.detectChanges();
          }
          } else {
          const patient = this.recentPatients.find(x => x.PatientId === this.globals.currentPatientId);
          if (patient) {
            patient.patientImageUrl = 'assets/Images/defaultPatient.PNG';
            this.cd.detectChanges();
          }
          }
        });
        });
      } else {
        const patient = this.recentPatients.find(x => x.PatientId === this.globals.currentPatientId);
        if (patient) {
        patient.patientImageUrl = 'assets/Images/defaultPatient.PNG';
        this.cd.detectChanges();
        }
      }
      }
    });

  }

  ngOnChanges(changes: SimpleChanges) {
    // Only refresh when the panel is opened
    if (changes.isOpen && changes.isOpen.currentValue === true) {
      this.isLoading = true;
      this.patientService.getRecentPatients(this.localStorageService.currentUserData.authdata.userid).subscribe(() => {
        this.recentPatients = [...this.patientService.recentPatients];
        this.isLoading = false;
        this.cd.markForCheck();
        this.cd.detectChanges();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.privacyMonitorSubscription) {
      this.privacyMonitorSubscription.unsubscribe();
    }
  }

  setupPrivacyMonitoring() {
    // Monitor privacy setting changes every 500ms
    this.privacyMonitorSubscription = interval(500).subscribe(() => {
      const currentPrivacySetting = this.globals.PrivacyEnabled;
      
      // Compare with previous value to detect changes
      if (currentPrivacySetting !== this.previousPrivacySetting) {
        // Update the globals setting
        this.globals.PrivacyEnabled = currentPrivacySetting;
        
        // Update our local privacy visibility
        this.isPrivateInfoVisible = !this.globals.PrivacyEnabled;
        
        // Update the previous value
        this.previousPrivacySetting = currentPrivacySetting;
        
        // Force change detection to update the UI
        this.cd.markForCheck();
        this.cd.detectChanges();
      }
    });
  }

  changePatient(patient: PatientDataModel) {
    if (this.globals.currentPatientId !== patient.PatientId) {
      this.messagingService.broadCastMessage(MessagingConstants.PATIENT_CHANGED, {
        PatientID: patient.PatientId,
        PatientName: patient.dataContact.FirstName + ' ' + patient.dataContact.MidName + ' ' + patient.dataContact.LastName,
        clearCurrentQuery: false
      });
      this.uiService.handlePatientSwitchImagingWindow();      
    }
    this.requestClose.emit();
  }

  // changePatientRange(isIncrementing: boolean) {
  //   this.homeAccordionService.changeRecentPatients(isIncrementing);
  // }

  getContact(appointment: PatientDataModel) {
    let comm = appointment.dataContact.dataContactComms.find(p => p.isPreferred === true);
    if (!comm) {
      if (appointment.dataContact.dataContactComms.length > 0) {
        comm = appointment.dataContact.dataContactComms.find(p => p.enumCommMethodID === CommMethodEnum.Mobile);
        if (comm) {
          return comm.Address;
        } else {
          comm = appointment.dataContact.dataContactComms.find(p => p.enumCommMethodID === CommMethodEnum.Email);
          if (comm) {
            return comm.Address;
          } else {
            comm = appointment.dataContact.dataContactComms.find(p => p.enumCommMethodID === CommMethodEnum.Phone);
            if (comm) {
              return comm.Address;
            } else {
              return 'No contact data found.';
            }
          }
        }
      } else {
        return '';
      }
    }
    return comm.Address;
  }

  getAddress(appointment: PatientDataModel) {
    if (appointment.dataContact.dataAddresses.length === 0) {
      return 'No address information';
    }

    // TODO: Adjust the query to not lazy load dataAddress and to filter out xauditDeleted on the backend.
    const comm = appointment.dataContact.dataAddresses.find(p => p.Address1 && !p.xauditDeleted);
    if (comm) {
      if (comm.Address1 && comm.Address1.length > 0) {
        return comm.Address1;
      }
    }

    return 'No address information';
  }

  getName(appt: PatientDataModel) {
    // Build full name first
    let fullName = appt.dataContact.FirstName;
    if (appt.dataContact.MidName) {
      fullName += ' ' + appt.dataContact.MidName;
    }
    fullName += ' ' + appt.dataContact.LastName;
    
    // Use the same masking logic as displayPatientName
    return this.displayPatientName(fullName);
  }

  /**
   * Displays patient name with masked last name
   * @param fullName - The full name of the patient
   * @returns First name with masked last name (e.g., "John D***")
   */
  displayPatientName(fullName: string): string {
    if (this.isPrivateInfoVisible) {
      return fullName;
    }
    if (!fullName || fullName.trim() === '') {
      return '';
    }

    const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);
    
    if (nameParts.length === 0) {
      return '';
    }
    
    if (nameParts.length === 1) {
      // Only one name part, return as is
      return nameParts[0];
    }
    
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    
    if (lastName.length <= 1) {
      // Last name is too short to mask
      return fullName;
    }
    
    const maskedLastName = lastName.charAt(0) + '*'.repeat(lastName.length - 1);
    
    // If there are middle names, include them
    if (nameParts.length > 2) {
      const middleNames = nameParts.slice(1, -1).join(' ');
      return `${firstName} ${middleNames} ${maskedLastName}`;
    }
    
    return `${firstName} ${maskedLastName}`;
  }

  async getPatientPicture(appt: PatientDataModel) {
    const res: DocumentDataModel = await this.fileService.getPatientPicture(appt.PatientId).toPromise();
    let url = 'assets/Images/defaultPatient.PNG';
    if (res) {
      url = this.fileService.getDocumentUrlFromDocument(res);
    }
    return url;
  }
}
